// FieldIQ — Chat tab — Lidy/Bob RAG chat

import { useState, useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import {
  MessageSquare,
  Loader2,
  Send,
  Mail,
  Search,
  History,
  PlusCircle,
  Trash2,
  ThumbsUp,
  ThumbsDown,
  Mic,
  WifiOff,
} from "lucide-react";
import { FormatMsg, SourceCitations, parseSources } from "../components/ui";
import { authFetch } from "../lib/api";
import {
  DEFAULT_QUICK_PROMPTS,
  DEFAULT_RAG_COLORS,
  getRagConfig,
  getUserInitials,
} from "../lib/helpers";
import {
  autoTitle,
  createConversation,
  deleteConversation,
  loadConversations,
  trackEvent,
  updateConversation,
} from "../lib/storage";

export const ChatTab = ({
  user,
  facility,
  prefill,
  onClearPrefill,
  autoSend,
  onClearAutoSend,
  isOnline,
}) => {
  if (!facility) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center">
        <div>
          <MessageSquare size={32} className="text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">No facility connected.</p>
          <p className="text-slate-600 text-xs mt-1">
            Contact your administrator.
          </p>
        </div>
      </div>
    );
  }
  if (!isOnline) {
    return (
      <div className="flex-1 flex items-center justify-center px-6 text-center">
        <div>
          <WifiOff size={32} className="text-slate-600 mx-auto mb-3" />
          <p className="text-slate-400 text-sm">
            {facility.agent_name || "Lidy"} is unavailable offline.
          </p>
          <p className="text-slate-600 text-xs mt-1">
            Reconnect to continue the conversation.
          </p>
        </div>
      </div>
    );
  }
  return (
    <ChatTabContent
      user={user}
      facility={facility}
      prefill={prefill}
      onClearPrefill={onClearPrefill}
      autoSend={autoSend}
      onClearAutoSend={onClearAutoSend}
    />
  );
};

const ChatTabContent = ({
  user,
  facility,
  prefill,
  onClearPrefill,
  autoSend,
  onClearAutoSend,
}) => {
  const fid = facility.facility_id;
  // Client portal RAG config lives in localStorage on a different origin,
  // so we read it here but it will only be populated if the user has also
  // visited the client portal on this same browser/device. The AI responses
  // are always identical — same /rag/chat endpoint and facility_id.
  const config = getRagConfig(fid);
  const colors = { ...DEFAULT_RAG_COLORS, ...config.colors };
  const ragSvc = facility.services?.find((s) => s.service_type === "rag");
  const serverTopics = ragSvc?.config?.quick_prompts || null;
  // Server config is authoritative; fall back to localStorage then hardcoded defaults
  const topics =
    serverTopics && Object.keys(serverTopics).length
      ? serverTopics
      : config.topics && Object.keys(config.topics).length
        ? config.topics
        : DEFAULT_QUICK_PROMPTS;
  const borderRadius = config.borderRadius || 12;
  const fontSize = config.fontSize || 14;
  const logoUrl = config.logoUrl || "";
  const botAvatarUrl =
    ragSvc?.config?.bot_avatar_url || config.botAvatarUrl || "";
  const br = `${borderRadius}px`;

  const agentName = facility.agent_name || facility.facility_name || "FieldIQ";
  const welcomeMsg = {
    role: "assistant",
    content: `Hi! I'm ${agentName}. What would you like to know?`,
    timestamp: new Date().toISOString(),
  };

  const [conversations, setConversations] = useState(() =>
    loadConversations(fid, user.email),
  );
  const [activeConvId, setActiveConvId] = useState(() => {
    const all = loadConversations(fid, user.email);
    if (all.length > 0) return all[0].id;
    const fresh = createConversation(fid, user.email, welcomeMsg);
    return fresh.id;
  });
  const [messages, setMessages] = useState(() => {
    const all = loadConversations(fid, user.email);
    const conv = all.find((c) => c.id === activeConvId);
    return conv ? conv.messages : [welcomeMsg];
  });
  // Use refs so these only fire on mount, not on re-renders
  const initialPrefillRef = useRef(prefill);
  const onClearPrefillRef = useRef(onClearPrefill);
  const autoSendRef = useRef(autoSend && !!prefill);
  const onClearAutoSendRef = useRef(onClearAutoSend);
  const [input, setInput] = useState(() => initialPrefillRef.current || "");
  const [sending, setSending] = useState(false);
  const [listening, setListening] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [emailStatus, setEmailStatus] = useState(null); // 'sent' | 'error' | null
  const [historyOpen, setHistoryOpen] = useState(false);
  const [historySearch, setHistorySearch] = useState("");
  const [sessionId, setSessionId] = useState(
    () => `rag-${fid}-${user.email}-${Date.now()}`,
  );
  const endRef = useRef(null);
  const recognitionRef = useRef(null);

  useEffect(() => {
    if (initialPrefillRef.current) onClearPrefillRef.current?.();
  }, []);
  const initials = getUserInitials(user);

  const SpeechRecognition =
    window.SpeechRecognition || window.webkitSpeechRecognition;
  const canVoice = !!SpeechRecognition;

  const toggleVoice = () => {
    if (listening) {
      recognitionRef.current?.stop();
      setListening(false);
      return;
    }
    const r = new SpeechRecognition();
    r.lang = "en-US";
    r.continuous = false;
    r.interimResults = true;
    r.onresult = (e) => {
      const transcript = Array.from(e.results)
        .map((res) => res[0].transcript)
        .join("");
      setInput(transcript);
    };
    r.onend = () => setListening(false);
    r.onerror = () => setListening(false);
    r.start();
    recognitionRef.current = r;
    setListening(true);
  };

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!activeConvId || messages.length === 0) return;
    const title = autoTitle(messages);
    const updated = updateConversation(fid, user.email, activeConvId, {
      messages,
      title,
    });
    setConversations(updated);
  }, [messages, activeConvId, fid, user.email]);

  const switchConversation = (convId) => {
    const all = loadConversations(fid, user.email);
    const conv = all.find((c) => c.id === convId);
    if (!conv) return;
    setActiveConvId(convId);
    setMessages(conv.messages);
    setSessionId(`rag-${fid}-${user.email}-${Date.now()}`);
    setInput("");
    setHistoryOpen(false);
  };

  const startNewChat = () => {
    const conv = createConversation(fid, user.email, welcomeMsg);
    setConversations(loadConversations(fid, user.email));
    setActiveConvId(conv.id);
    setMessages([welcomeMsg]);
    setSessionId(`rag-${fid}-${user.email}-${Date.now()}`);
    setInput("");
    setHistoryOpen(false);
  };

  useEffect(() => {
    const key = "fieldiq_chat_cleared";
    if (!sessionStorage.getItem(key)) {
      sessionStorage.setItem(key, "1");
      startNewChat();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const removeConversation = (convId) => {
    const updated = deleteConversation(fid, user.email, convId);
    setConversations(updated);
    if (convId === activeConvId) {
      updated.length > 0 ? switchConversation(updated[0].id) : startNewChat();
    }
  };

  const handleFeedback = (msgIndex, feedback) => {
    const logId = messages[msgIndex]?.conversationLogId ?? null;
    setMessages((prev) => {
      const next = [...prev];
      next[msgIndex] = { ...next[msgIndex], feedback };
      return next;
    });
    authFetch("/fieldiq/feedback", {
      method: "POST",
      body: JSON.stringify({
        facility_id: fid,
        session_id: activeConvId || null,
        rating: feedback === "up" ? 1 : -1,
        // Ties the rating to the turn, so the portal can show the question and
        // answer that earned it rather than a bare thumbs-down.
        conversation_log_id: logId,
      }),
    }).catch(() => {});
  };

  const emailTranscript = async () => {
    if (emailing) return;
    const userMessages = messages.filter((m) => m.role === "user");
    if (userMessages.length === 0) {
      setEmailStatus("error");
      setTimeout(() => setEmailStatus(null), 2500);
      return;
    }
    setEmailing(true);
    const payload = {
      messages: messages.map((m) => ({ role: m.role, text: m.content })),
      facility_id: fid,
      agent_name: agentName,
    };
    const res = await authFetch("/email-transcript", {
      method: "POST",
      body: JSON.stringify(payload),
    });
    setEmailing(false);
    if (res.success) {
      setEmailStatus("sent");
    } else {
      setEmailStatus("error");
    }
    setTimeout(() => setEmailStatus(null), 3000);
  };

  const send = async (text) => {
    const msg = text || input.trim();
    if (!msg || sending) return;
    setMessages((p) => [
      ...p,
      { role: "user", content: msg, timestamp: new Date().toISOString() },
    ]);
    setInput("");
    setSending(true);
    trackEvent(fid, "query", { user: user.email });
    const sendStart = Date.now();
    try {
      const data = await authFetch("/rag/chat", {
        method: "POST",
        body: JSON.stringify({ facility_id: fid, chatInput: msg, sessionId }),
      });
      if (data.error) throw new Error(data.error);
      const sources = parseSources(data);
      trackEvent(fid, "response", {
        responseTime: Date.now() - sendStart,
        sourceCount: sources.length,
      });
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          content:
            data.output || data.text || data.response || JSON.stringify(data),
          timestamp: new Date().toISOString(),
          sources,
          // Already in the response — /rag/chat passes the n8n payload through
          // untouched. Keeping it lets a rating point at the exact turn instead
          // of just the session. Null on a cached answer, which the workflow
          // answers without writing a conversation log.
          conversationLogId: data.conversation_log_id ?? null,
        },
      ]);
    } catch (err) {
      trackEvent(fid, "error", { message: err.message });
      setMessages((p) => [
        ...p,
        {
          role: "assistant",
          content: `Error: ${err.message}`,
          timestamp: new Date().toISOString(),
          isError: true,
        },
      ]);
    }
    setSending(false);
  };

  // Auto-send when navigating from "Ask Lidy about X" or a rep name
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (autoSendRef.current) {
      onClearAutoSendRef.current?.();
      startNewChat();
      send(initialPrefillRef.current);
    }
  }, []);

  const filteredConversations = historySearch
    ? conversations.filter(
        (c) =>
          c.title.toLowerCase().includes(historySearch.toLowerCase()) ||
          c.messages.some(
            (m) =>
              m.role === "user" &&
              m.content.toLowerCase().includes(historySearch.toLowerCase()),
          ),
      )
    : conversations;

  // Flattened quick prompts for mobile chip row
  const allPrompts = Object.values(topics).flat().slice(0, 6);

  const BotAvatar = () =>
    botAvatarUrl ? (
      <img
        src={botAvatarUrl}
        alt="AI"
        className="w-7 h-7 rounded-full object-cover flex-shrink-0"
        onError={(e) => {
          e.target.style.display = "none";
        }}
      />
    ) : (
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
        style={{ backgroundColor: colors.accent, color: "#fff" }}
      >
        AI
      </div>
    );

  return (
    <div
      className="h-full flex flex-col"
      style={{ backgroundColor: colors.bg }}
    >
      {/* Header */}
      <div
        className="px-3 py-2 flex items-center justify-between flex-shrink-0"
        style={{
          backgroundColor: colors.sidebar,
          borderBottom: `1px solid ${colors.accent}33`,
        }}
      >
        <div className="flex items-center gap-2 min-w-0">
          {logoUrl && (
            <img
              src={logoUrl}
              alt=""
              className="h-6 object-contain flex-shrink-0"
              onError={(e) => {
                e.target.style.display = "none";
              }}
            />
          )}
          <div className="min-w-0">
            <p
              className="text-xs font-semibold truncate"
              style={{ color: colors.headings }}
            >
              {agentName}
            </p>
            <div className="flex items-center gap-1">
              <span className="w-1.5 h-1.5 rounded-full bg-green-500" />
              <span
                className="text-xs"
                style={{ color: colors.text, opacity: 0.6 }}
              >
                {facility?.kb_last_updated
                  ? `KB updated ${new Date(facility.kb_last_updated + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`
                  : "KB Active"}
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {emailStatus && (
            <span
              className="text-xs px-2 py-0.5 rounded-full"
              style={{
                backgroundColor:
                  emailStatus === "sent" ? "#22c55e20" : "#ef444420",
                color: emailStatus === "sent" ? "#22c55e" : "#f87171",
              }}
            >
              {emailStatus === "sent" ? "Sent!" : "Failed"}
            </span>
          )}
          <button
            onClick={emailTranscript}
            disabled={emailing}
            className="p-1.5 rounded-lg transition-colors"
            style={{
              color: colors.accent,
              backgroundColor: `${colors.accent}15`,
              opacity: emailing ? 0.5 : 1,
            }}
            title="Email this conversation"
          >
            {emailing ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Mail className="w-4 h-4" />
            )}
          </button>
          <button
            onClick={() => setHistoryOpen(!historyOpen)}
            className="p-1.5 rounded-lg transition-colors"
            style={{
              color: historyOpen ? "#fff" : colors.accent,
              backgroundColor: historyOpen
                ? colors.accent
                : `${colors.accent}15`,
            }}
            title="Conversation history"
          >
            <History className="w-4 h-4" />
          </button>
          <button
            onClick={startNewChat}
            className="p-1.5 rounded-lg transition-colors"
            style={{
              color: colors.accent,
              backgroundColor: `${colors.accent}15`,
            }}
            title="New conversation"
          >
            <PlusCircle className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Conversation history drawer */}
      {historyOpen && (
        <div
          className="border-b overflow-y-auto flex-shrink-0"
          style={{
            backgroundColor: colors.sidebar,
            borderColor: `${colors.accent}33`,
            maxHeight: "220px",
          }}
        >
          <div className="px-3 py-2">
            <div
              className="flex items-center px-2 py-1.5 mb-2"
              style={{
                backgroundColor: colors.chatBg,
                borderRadius: "8px",
                border: `1px solid ${colors.accent}30`,
              }}
            >
              <Search
                className="w-3.5 h-3.5 mr-2 flex-shrink-0"
                style={{ color: colors.accent }}
              />
              <input
                value={historySearch}
                onChange={(e) => setHistorySearch(e.target.value)}
                placeholder="Search conversations..."
                className="flex-1 bg-transparent outline-none text-xs"
                style={{ color: colors.headings }}
              />
            </div>
            {filteredConversations.length === 0 ? (
              <p
                className="text-xs text-center py-3"
                style={{ color: colors.text, opacity: 0.5 }}
              >
                No conversations found
              </p>
            ) : (
              <div className="space-y-1">
                {filteredConversations.map((conv) => (
                  <div
                    key={conv.id}
                    className="flex items-center group rounded-lg"
                    style={{
                      backgroundColor:
                        conv.id === activeConvId
                          ? `${colors.accent}25`
                          : "transparent",
                    }}
                  >
                    <button
                      onClick={() => switchConversation(conv.id)}
                      className="flex-1 text-left px-3 py-2 min-w-0"
                    >
                      <p
                        className="text-xs font-medium truncate"
                        style={{ color: colors.headings }}
                      >
                        {conv.title}
                      </p>
                      <p
                        className="text-xs truncate"
                        style={{ color: colors.text, opacity: 0.5 }}
                      >
                        {conv.messages.filter((m) => m.role === "user").length}{" "}
                        messages
                        {conv.updatedAt &&
                          ` · ${new Date(conv.updatedAt).toLocaleDateString()}`}
                      </p>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        removeConversation(conv.id);
                      }}
                      className="p-1.5 opacity-0 group-hover:opacity-100 transition-opacity mr-1"
                      style={{ color: "#ef4444" }}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Quick prompt chips — horizontal scroll for mobile */}
      {!historyOpen && (
        <div
          className="flex gap-2 px-3 py-2 overflow-x-auto flex-shrink-0"
          style={{ borderBottom: `1px solid ${colors.accent}22` }}
        >
          {allPrompts.map((prompt, i) => (
            <button
              key={i}
              onClick={() => send(prompt)}
              disabled={sending}
              className="flex-shrink-0 px-3 py-1 rounded-full transition-all hover:opacity-80"
              style={{
                backgroundColor: `${colors.accent}20`,
                color: colors.text,
                border: `1px solid ${colors.accent}30`,
                fontSize: "11px",
                whiteSpace: "nowrap",
              }}
            >
              {prompt.length > 32 ? prompt.slice(0, 32) + "…" : prompt}
            </button>
          ))}
        </div>
      )}

      {/* Messages */}
      <div
        className="flex-1 overflow-y-auto px-3 py-3 space-y-3"
        style={{ backgroundColor: colors.chatBg }}
      >
        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"} items-end gap-2`}
          >
            {msg.role !== "user" && <BotAvatar />}
            <div className="max-w-[85%]">
              <div
                className="px-3 py-2.5 leading-relaxed"
                style={{
                  backgroundColor:
                    msg.role === "user"
                      ? colors.userBubble
                      : msg.isError
                        ? "#3b1515"
                        : colors.botBubble,
                  color: msg.isError ? "#f87171" : colors.headings,
                  border: `1px solid ${msg.isError ? "#ef444433" : colors.accent + "30"}`,
                  borderRadius:
                    msg.role === "user"
                      ? `${br} 0 ${br} ${br}`
                      : `0 ${br} ${br} ${br}`,
                  fontSize: `${fontSize}px`,
                  lineHeight: "1.65",
                }}
              >
                {msg.role === "assistant" &&
                !msg.isError &&
                /<[a-z][^>]*>/i.test(msg.content) ? (
                  <div
                    className="enriched-response"
                    // Content is sanitized with DOMPurify before rendering
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(msg.content),
                    }}
                  />
                ) : (
                  <FormatMsg text={msg.content} accentColor={colors.accent} />
                )}
              </div>
              {msg.sources && msg.sources.length > 0 && (
                <SourceCitations
                  sources={msg.sources}
                  colors={colors}
                  borderRadius={borderRadius}
                />
              )}
              {msg.role === "assistant" && !msg.isError && i > 0 && (
                <div className="flex items-center gap-1 mt-1 ml-1">
                  <button
                    onClick={() => handleFeedback(i, "up")}
                    className="p-1 rounded transition-colors"
                    style={{
                      color:
                        msg.feedback === "up" ? "#22c55e" : `${colors.text}50`,
                      backgroundColor:
                        msg.feedback === "up" ? "#22c55e15" : "transparent",
                    }}
                  >
                    <ThumbsUp className="w-3 h-3" />
                  </button>
                  <button
                    onClick={() => handleFeedback(i, "down")}
                    className="p-1 rounded transition-colors"
                    style={{
                      color:
                        msg.feedback === "down"
                          ? "#ef4444"
                          : `${colors.text}50`,
                      backgroundColor:
                        msg.feedback === "down" ? "#ef444415" : "transparent",
                    }}
                  >
                    <ThumbsDown className="w-3 h-3" />
                  </button>
                </div>
              )}
            </div>
            {msg.role === "user" && (
              <div
                className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0"
                style={{
                  backgroundColor: colors.userBubble,
                  color: colors.headings,
                  border: `1px solid ${colors.accent}40`,
                }}
              >
                {initials}
              </div>
            )}
          </div>
        ))}
        {sending && (
          <div className="flex justify-start items-end gap-2">
            <BotAvatar />
            <div
              className="px-3 py-2.5"
              style={{
                backgroundColor: colors.botBubble,
                border: `1px solid ${colors.accent}30`,
                borderRadius: `0 ${br} ${br} ${br}`,
              }}
            >
              <div className="flex space-x-1.5">
                {[0, 1, 2].map((d) => (
                  <div
                    key={d}
                    className="w-2 h-2 rounded-full animate-bounce"
                    style={{
                      backgroundColor: colors.accent,
                      animationDelay: `${d * 150}ms`,
                    }}
                  />
                ))}
              </div>
            </div>
          </div>
        )}
        <div ref={endRef} />
      </div>

      {/* Input bar */}
      <div
        className="px-3 py-2.5 flex gap-2 flex-shrink-0"
        style={{
          backgroundColor: colors.bg,
          borderTop: `1px solid ${colors.accent}33`,
        }}
      >
        <input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) =>
            e.key === "Enter" && !e.shiftKey && (e.preventDefault(), send())
          }
          placeholder={listening ? "Listening…" : "Ask a question..."}
          disabled={sending}
          className="flex-1 px-4 py-2.5 outline-none text-sm"
          style={{
            backgroundColor: colors.chatBg,
            color: colors.headings,
            border: `1px solid ${listening ? "#ef4444" : `${colors.accent}40`}`,
            borderRadius: br,
          }}
        />
        {canVoice && (
          <button
            onClick={toggleVoice}
            disabled={sending}
            title={listening ? "Stop listening" : "Speak your question"}
            className="p-2.5 flex-shrink-0 transition-colors"
            style={{
              backgroundColor: listening ? "#ef4444" : `${colors.accent}25`,
              color: listening ? "#fff" : colors.text,
              borderRadius: br,
              border: `1px solid ${listening ? "#ef4444" : `${colors.accent}40`}`,
            }}
          >
            <Mic className={`w-5 h-5 ${listening ? "animate-pulse" : ""}`} />
          </button>
        )}
        <button
          onClick={() => send()}
          disabled={sending || !input.trim()}
          className="p-2.5 transition-colors flex-shrink-0"
          style={{
            backgroundColor:
              !sending && input.trim() ? colors.accent : `${colors.accent}40`,
            color: "#fff",
            borderRadius: br,
            border: "none",
          }}
        >
          {sending ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <Send className="w-5 h-5" />
          )}
        </button>
      </div>
    </div>
  );
};
